import { AppThemeMode, BrandConfig } from "./brandConfig";

const STYLE_TAG_ID = "dynamic-brand-global-style";
const FONT_LINK_ID = "dynamic-brand-font-link";

const normalizeFontFamilyName = (fontFamily: string): string => {
  const firstFamily = fontFamily.split(",")[0].trim();
  return firstFamily.replace(/^['\"]|['\"]$/g, "");
};

const ensureFontLoaded = (fontFamily: string): void => {
  if (typeof document === "undefined") return;

  const cleanName = normalizeFontFamilyName(fontFamily);
  if (!cleanName) return;

  const encoded = cleanName.trim().replace(/\s+/g, "+");
  const href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@300;400;500;600;700&display=swap`;

  let linkEl = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  if (!linkEl) {
    linkEl = document.createElement("link");
    linkEl.id = FONT_LINK_ID;
    linkEl.rel = "stylesheet";
    document.head.appendChild(linkEl);
  }

  linkEl.href = href;
};

export const applyGlobalBranding = (
  config: BrandConfig,
  mode: AppThemeMode = "light"
): void => {
  if (typeof document === "undefined") return;
  const fontFamily = (config.fontFamily || "").trim();
  const palette = mode === "dark" ? config.dark : config.light;
  ensureFontLoaded(fontFamily);

  const root = document.documentElement;

  root.style.setProperty("--app-primary-color", palette.primaryColor);
  root.style.setProperty("--app-secondary-color", palette.secondaryColor);
  root.style.setProperty("--app-text-color", palette.textColor);
  root.style.setProperty("--app-font-family", fontFamily);
  // Keep compatibility with existing app font tokens.
  root.style.setProperty("--font-raleway", fontFamily);
  root.style.setProperty("--font-lato", fontFamily);
  root.style.setProperty("--font-red-hat-display", fontFamily);

  document.body.style.fontFamily = "var(--app-font-family)";
  document.body.style.color = "var(--app-text-color)";

  let styleEl = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = STYLE_TAG_ID;
    document.head.appendChild(styleEl);
  }

  styleEl.textContent = `
    :root {
      --app-primary-color: ${palette.primaryColor};
      --app-secondary-color: ${palette.secondaryColor};
      --app-text-color: ${palette.textColor};
      --app-font-family: ${fontFamily};
      --font-raleway: ${fontFamily};
      --font-lato: ${fontFamily};
      --font-red-hat-display: ${fontFamily};
    }

    html, body, #root {
      font-family: var(--app-font-family) !important;
      color: var(--app-text-color);
    }

    *, *::before, *::after {
      font-family: var(--app-font-family) !important;
    }
  `;
};
